import { Controller, type UseFormReturn } from "react-hook-form";
import { type CompleteRegistrationFormValues } from "./CompleteRegistrationPage";
import { Row } from "../common/components/Layout";
import { SimpleSelect } from "../common/components/Select/SimpleSelect";
import { useMemo } from "react";
import { MaterialInput } from "../common/components/materialInput/MaterialInput";
import Parameter from "../common/components/Parameter";
import PasswordInput from "../common/components/materialInput/PasswordInput";
import { IconType } from "../common/components/icon/IconType";
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PASSWORD_REGEXP,
  PHONE_REGEXP,
  ZIPCODE_MAX_LENGTH,
  ZIPCODE_MIN_LENGTH,
  ZIPCODE_REGEXP,
} from "../utils/validations";
import { SimpleAutocomplete } from "../common/components/Select/SimpleAutocompleteSelect";
import { withClass } from "../common/hoc/withClass";
import { FormSection } from "../MyAccount/MyAccountContainer";
import { AffiliateFormSection } from "~/feature/Affiliate/AffiliateFormSection";
import { type AffiliatePartnerData } from "~/feature/Affiliate/models";
import { type DropdownEntry } from "~/common/types";
import { useTranslation } from "react-i18next";

import { isNonNull } from "~/utils/reducers";
import { TrainingConsent } from "~/MyAccount/components/TrainingConsent";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";

import classes from "./CompleteRegistrationForm.module.less";
import trainingClasses from "../MyAccount/components/TrainingConsent.module.less";
import { makeSelectRef4RHF } from "~/MyAccount/formUtils";
import { Alert } from "~/common/m3/Alert/Alert";
import { Stack } from "@mui/material";

interface Props extends UseFormReturn<CompleteRegistrationFormValues> {
  email: string;
  initialCustomerCategory?: string;
  titleOptions: DropdownEntry<string>[];
  salutationOptions: DropdownEntry<string>[];
  countryOptions: DropdownEntry<string>[];
  industryOptions: DropdownEntry<string>[];
  jobTitleOptions: DropdownEntry<string>[];
  functionalAreaOptions: DropdownEntry<string>[];
  salesPartner?: AffiliatePartnerData;
  isEnableFieldsTraining: boolean;
  customerCategoryOptions: DropdownEntry<string>[];
  knowledgeAreaOptions: DropdownEntry<string>[];
  languageOptions: DropdownEntry<string>[];
}

export const CompleteRegistrationForm = ({
  email,
  initialCustomerCategory,
  titleOptions,
  salutationOptions,
  countryOptions,
  industryOptions,
  jobTitleOptions,
  functionalAreaOptions,
  salesPartner,
  register,
  control,
  watch,
  getValues,
  trigger,
  setValue,
  customerCategoryOptions,
  knowledgeAreaOptions,
  languageOptions,
  isEnableFieldsTraining,
}: Props) => {
  const { t } = useTranslation();
  const getLabel = (name: string, required?: boolean) => t(`page.completeRegistration.${name}`) + (required ? "*" : "");
  const customerCategory = watch("customerCategory");

  const pleaseEnterError = (fieldName: string) => t("page.profile.pleaseEnter." + fieldName);

  const affiliates = useMemo(() => [salesPartner].filter(isNonNull), [salesPartner]);

  // after clean up move to CompleteRegistrationPage.tsx line 85 (formProps defaultValues)
  if (initialCustomerCategory && customerCategory !== initialCustomerCategory) {
    setValue("customerCategory", initialCustomerCategory);
  }

  const isMinDesktop = useIsMinDesktopSize();

  return (
    <>
      <Row>
        <Column>
          <FormSection title={t("myAccount.name.title")}>
            <Controller
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <SimpleSelect
                  values={titleOptions}
                  label={getLabel("customerTitle", false)}
                  error={!!error}
                  helperText={error?.message}
                  fullWidth={true}
                  data-test-id={"complete-registration-title"}
                  onBlur={onBlur}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                />
              )}
              name={"title"}
              control={control}
            />
            <Controller
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <SimpleSelect
                  values={salutationOptions}
                  label={getLabel("salutation", true)}
                  error={!!error}
                  helperText={error?.message}
                  fullWidth={true}
                  data-test-id={"complete-registration-salutation"}
                  onBlur={onBlur}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                />
              )}
              name={"salutation"}
              control={control}
              rules={{ required: pleaseEnterError("salutation") }}
            />

            <Controller
              control={control}
              name="firstName"
              rules={{ required: pleaseEnterError("firstName") }}
              render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                <MaterialInput
                  label={getLabel("firstName", true)}
                  inputRef={ref}
                  helperLabel={error?.message ?? true}
                  colorHelperOnError={true}
                  error={!!error}
                  data-test-id={"complete-registration-firstName"}
                  autoComplete={"given-name"}
                  {...inputProps}
                />
              )}
            />

            <Controller
              control={control}
              name="lastName"
              rules={{ required: pleaseEnterError("lastName") }}
              render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                <MaterialInput
                  label={getLabel("lastName", true)}
                  data-test-id={"complete-registration-lastName"}
                  inputRef={ref}
                  helperLabel={error?.message ?? true}
                  colorHelperOnError={true}
                  error={!!error}
                  autoComplete={"last-name"}
                  {...inputProps}
                />
              )}
            />

            <Parameter label={getLabel("email")} value={<div className="title-medium">{email}</div>} />
            <FormSection title={t("page.completeRegistration.password")} className={classes.password}>
              <Controller
                name={"password"}
                control={control}
                rules={{
                  // required: {value: true, message: t("page.completeRegistration.errorMatchRequirements")},
                  required: { value: true, message: pleaseEnterError("password") },
                  minLength: {
                    value: PASSWORD_MIN_LENGTH,
                    message: t("page.completeRegistration.errorMatchRequirements"),
                  },
                  maxLength: {
                    value: PASSWORD_MAX_LENGTH,
                    message: t("page.completeRegistration.errorMatchRequirements"),
                  },
                  pattern: { value: PASSWORD_REGEXP, message: t("page.completeRegistration.errorMatchRequirements") },
                }}
                render={({ field: { ref, onBlur, ...inputProps }, fieldState: { error } }) => (
                  <PasswordInput
                    label={getLabel("password", true)}
                    error={!!error}
                    helperLabel={error?.message}
                    iconType={IconType.EyeOff}
                    iconTypeAfterClick={IconType.Eye}
                    data-test-id={"complete-registration-password"}
                    onBlur={() => {
                      onBlur();
                      trigger(["password", "confirmPassword"]);
                    }}
                    autoComplete={"new-password"}
                    inputRef={ref}
                    {...inputProps}
                  />
                )}
              />

              <Controller
                name={"confirmPassword"}
                control={control}
                rules={{
                  required: { value: true, message: pleaseEnterError("password") },
                  validate: (val) =>
                    val === getValues().password || (t("page.completeRegistration.errorTwoPasswordsMatch")! as string),
                }}
                render={({ field: { ref, onBlur, ...inputProps }, fieldState: { error } }) => (
                  <PasswordInput
                    label={getLabel("confirmPassword", true)}
                    error={!!error}
                    helperLabel={error?.message}
                    iconType={IconType.EyeOff}
                    iconTypeAfterClick={IconType.Eye}
                    data-test-id={"complete-registration-confirmPassword"}
                    onBlur={() => {
                      onBlur();
                      trigger(["password", "confirmPassword"]);
                    }}
                    autoComplete={"new-password"}
                    inputRef={ref}
                    {...inputProps}
                  />
                )}
              />

              <Alert severity={"info"}>{t("page.completeRegistration.passwordInfo")}</Alert>
            </FormSection>
          </FormSection>

          {salesPartner && (
            <AffiliateFormSection
              control={control}
              register={register}
              setValue={setValue}
              watch={watch}
              affiliates={affiliates}
            />
          )}
        </Column>

        <Column>
          <FormSection title={t("myAccount.company.title")}>
            <Controller
              name={"company"}
              control={control}
              rules={{ required: { value: true, message: pleaseEnterError("companyName") } }}
              render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                <MaterialInput
                  label={getLabel("companyName", true)}
                  data-test-id={"complete-registration-company"}
                  autoComplete={"organization"}
                  colorHelperOnError={true}
                  error={!!error}
                  helperLabel={error?.message ?? true}
                  inputRef={ref}
                  {...inputProps}
                />
              )}
            />

            <Controller
              render={({ field: { ref, value, onChange, onBlur }, fieldState: { error } }) => (
                <SimpleSelect
                  values={industryOptions}
                  label={getLabel("industrySector", true)}
                  fullWidth={true}
                  data-test-id={"complete-registration-industry"}
                  onBlur={onBlur}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  error={!!error}
                  helperText={error?.message ?? ""}
                  ref={makeSelectRef4RHF(ref)}
                />
              )}
              name={"industrySector"}
              control={control}
              rules={{ required: pleaseEnterError("industrySector") }}
            />

            <Controller
              render={({ field: { value, onChange, onBlur } }) => (
                <SimpleSelect
                  values={functionalAreaOptions}
                  label={getLabel("functionalArea")}
                  fullWidth={true}
                  data-test-id={"complete-registration-functionalArea"}
                  onBlur={onBlur}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                />
              )}
              name={"functionalArea"}
              control={control}
            />

            {isEnableFieldsTraining && (
              <Alert severity={"info"} className={"mb-3"}>
                {t("page.completeRegistration.contactInfo")}
              </Alert>
            )}

            <Controller
              name={"jobTitle"}
              control={control}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <SimpleSelect
                  values={jobTitleOptions}
                  label={getLabel("jobTitle", false)}
                  fullWidth={true}
                  data-test-id={"complete-registration-jobTitle"}
                  onBlur={onBlur}
                  value={value}
                  error={!!error}
                  onChange={(e) => onChange(e.target.value)}
                  helperText=""
                />
              )}
            />

            <Controller
              name={"address"}
              control={control}
              rules={{
                required: isEnableFieldsTraining,
              }}
              render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                <MaterialInput
                  label={getLabel("address", isEnableFieldsTraining)}
                  data-test-id={"complete-registration-address"}
                  autoComplete={"address-line1"}
                  error={!!error}
                  inputRef={ref}
                  helperLabel
                  {...inputProps}
                />
              )}
            />

            <Controller
              name={"addressAdditional"}
              control={control}
              render={({ field: { ref, ...inputProps } }) => (
                <MaterialInput
                  label={getLabel("addressAdditional")}
                  data-test-id={"complete-registration-addressAdditional"}
                  autoComplete={"address-line2"}
                  inputRef={ref}
                  {...inputProps}
                />
              )}
            />

            <div className="scha__my-account-form__field--zip-city">
              <Controller
                name={"zipCode"}
                control={control}
                rules={{
                  minLength: {
                    value: ZIPCODE_MIN_LENGTH,
                    message: t("checkoutPage.addressModal.error.isTooShortPart", {
                      fieldname: t(`checkoutPage.addressModal.input.label.zipCode`),
                    }),
                  },
                  maxLength: {
                    value: ZIPCODE_MAX_LENGTH,
                    message: t("checkoutPage.addressModal.error.isTooLongPart", {
                      fieldname: t(`checkoutPage.addressModal.input.label.zipCode`),
                    }),
                  },
                  pattern: {
                    value: ZIPCODE_REGEXP,
                    message: t("checkoutPage.addressModal.error.forbidenCharactersPart", {
                      fieldname: t(`checkoutPage.addressModal.input.label.zipCode`),
                    }),
                  },
                  required: isEnableFieldsTraining,
                }}
                render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                  <MaterialInput
                    label={getLabel("zipCode", isEnableFieldsTraining)}
                    data-test-id={"complete-registration-zipCode"}
                    error={!!error}
                    helperLabel={error?.message}
                    onBeforeInput={(e) => {
                      // @ts-ignore
                      const val = e.target.value + e.data;
                      if (!ZIPCODE_REGEXP.test(val.trim())) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                    autoComplete={"postal-code"}
                    inputRef={ref}
                    {...inputProps}
                  />
                )}
              />

              <Controller
                name={"city"}
                control={control}
                rules={{
                  required: isEnableFieldsTraining,
                }}
                render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                  <MaterialInput
                    label={getLabel("city", isEnableFieldsTraining)}
                    data-test-id={"complete-registration-city"}
                    autoComplete={"city"}
                    inputRef={ref}
                    error={!!error}
                    {...inputProps}
                  />
                )}
              />
            </div>

            <Controller
              name={"country"}
              control={control}
              rules={{ required: { value: true, message: t("validation.country.missing") } }}
              defaultValue={""}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <SimpleAutocomplete
                  label={t("STCfinder.table.header.country") + "*"}
                  data-test-id={"complete-registration-country"}
                  values={countryOptions}
                  fullWidth={true}
                  error={!!error}
                  helperText={error?.message ?? " "}
                  onBlur={onBlur}
                  value={value}
                  onChange={(value) => onChange(value)}
                />
              )}
            />

            <Controller
              name={"phone"}
              control={control}
              render={({ field: { ref, ...inputProps } }) => (
                <MaterialInput
                  label={getLabel("phone")}
                  helperLabel={getLabel("examplePhone")}
                  onBeforeInput={(e) => {
                    // @ts-ignore
                    const val = e.target.value + e.data;
                    if (!PHONE_REGEXP.test(val.trim())) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  data-test-id={"complete-registration-phone"}
                  autoComplete={"tel"}
                  inputRef={ref}
                  {...inputProps}
                />
              )}
            />

            {isEnableFieldsTraining && (
              <Controller
                name={"vat"}
                control={control}
                render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                  <MaterialInput
                    label={getLabel("vat")}
                    helperLabel={getLabel("exampleVat")}
                    data-test-id={"complete-registration-vat"}
                    error={!!error}
                    inputRef={ref}
                    {...inputProps}
                  />
                )}
              />
            )}

            <Alert severity={"info"}>{t("page.completeRegistration.contactInfo")}</Alert>
          </FormSection>
        </Column>

        <Column>
          {isMinDesktop && (
            <div className="d-flex">
              <h3 className={"scha__my-account-form__section__title mr-16"}>
                {t("page.registerForFree.baner.trainingCampus")}
              </h3>
              <div className={trainingClasses.flag}>{t("myAccount.training.flag.label")}</div>
            </div>
          )}
          <TrainingConsent
            checked={isEnableFieldsTraining}
            disabled={true}
            className={"scha__complete-registration__training"}
          />

          {isEnableFieldsTraining && (
            <>
              <p className="my-30 title-medium">{t("myAccount.training.welcome")}</p>
              <Stack gap={2}>
                <Controller
                  rules={{
                    required: isEnableFieldsTraining,
                  }}
                  render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                    <SimpleSelect
                      values={languageOptions}
                      label={getLabel("language", isEnableFieldsTraining)}
                      fullWidth={true}
                      error={!!error}
                      data-test-id={"complete-registration-language"}
                      onBlur={onBlur}
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                    />
                  )}
                  name={"language"}
                  control={control}
                />

                <Controller
                  rules={{
                    required: isEnableFieldsTraining,
                  }}
                  render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                    <SimpleSelect
                      values={customerCategoryOptions}
                      label={getLabel("customerCategory", isEnableFieldsTraining)}
                      fullWidth={true}
                      error={!!error}
                      data-test-id={"complete-registration-customer-category"}
                      onBlur={onBlur}
                      value={value}
                      disabled={!!initialCustomerCategory}
                      onChange={(e) => onChange(e.target.value)}
                    />
                  )}
                  name={"customerCategory"}
                  control={control}
                />
                {!initialCustomerCategory && (
                  <Alert severity={"info"}>{t("page.completeRegistration.categoryInfo")}</Alert>
                )}

                <Controller
                  rules={{
                    required: isEnableFieldsTraining,
                  }}
                  render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                    <SimpleSelect
                      values={knowledgeAreaOptions}
                      label={getLabel("knowledgeArea", isEnableFieldsTraining)}
                      fullWidth={true}
                      error={!!error}
                      data-test-id={"complete-registration-knowledge-area"}
                      onBlur={onBlur}
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                    />
                  )}
                  name={"knowledgeArea"}
                  control={control}
                />
              </Stack>
            </>
          )}
        </Column>
      </Row>

      <div className={"mb-30"}>{t("page.completeRegistration.mandatory")}</div>
    </>
  );
};

const Column = withClass("col-md-4 col-sm-12")("div");
